// Host-side action seeding.
//
// Called each render by SceneDesignerTool — produces the merged ActionMap
// the layouts and ActionBlocks consume. Built-ins win over project-registered
// entries with the same id (see registry.ts rationale).
//
// Built-in catalog matches .design.md "Built-in actions (day one)":
//   reset, delete, undo, redo, rotate-clockwise, rotate-counter-clockwise,
//   camera-toggle, measurements-toggle, help, open-cart, close-cart,
//   back-to-series.
//
// Adding a new built-in = a new entry below. Wiring is a function of host
// state + the ActionHostApi the host already exposes.

import { useMemo } from "react";
import { materializeRegistered } from "./registry";
import type { ActionDef, ActionHostApi, ActionMap } from "./types";

export interface UseHostActionsArgs {
  host: ActionHostApi;
  /** Whether the measurements scene-feature is on right now. Lifted to a
   *  prop so the hook doesn't reach into the feature store directly — keeps
   *  the dependency list explicit. Will move into host.getFeature once the
   *  full sceneFeatures slice lands in step 8. */
  measurementsVisible: boolean;
}

export function useHostActions({ host, measurementsVisible }: UseHostActionsArgs): ActionMap {
  return useMemo(() => {
    const map = new Map<string, ActionDef>();

    // Project registrations first; built-ins overwrite by id below.
    for (const r of materializeRegistered(host)) {
      map.set(r.id, r);
    }

    const builtins: ActionDef[] = [
      {
        id: "reset",
        label: "Reset scene",
        icon: "refresh",
        run: () => host.resetScene(),
        enabled: host.instanceCount > 0,
        confirm: {
          title: "Reset the scene?",
          message: "This removes every placed element from the configurator. The action can't be undone.",
          confirmLabel: "Reset",
        },
      },
      {
        id: "delete",
        label: "Delete selection",
        icon: "delete",
        run: () => host.deleteSelected(),
        enabled: host.selectedInstanceId != null,
      },
      {
        id: "undo",
        label: "Undo",
        icon: "undo",
        run: () => host.sendCommand("Undo"),
      },
      {
        id: "redo",
        label: "Redo",
        icon: "redo",
        run: () => host.sendCommand("Redo"),
      },
      {
        id: "rotate-clockwise",
        label: "Rotate clockwise",
        icon: "rotate_right",
        run: () => host.sendCommand("RotateClockwise"),
        enabled: host.selectedInstanceId != null,
      },
      {
        id: "rotate-counter-clockwise",
        label: "Rotate counter-clockwise",
        icon: "rotate_left",
        run: () => host.sendCommand("RotateCounterClockwise"),
        enabled: host.selectedInstanceId != null,
      },
      {
        id: "camera-toggle",
        // Icon + label reflect the *current* mode; clicking switches.
        label: host.isFpsCamera ? "Switch to top-down camera" : "Switch to walking camera",
        icon: host.isFpsCamera ? "directions_walk" : "landscape",
        active: host.isFpsCamera,
        run: () => host.sendCommand(host.isFpsCamera ? "EnableTopDownCamera" : "EnableFPSCamera"),
      },
      {
        id: "measurements-toggle",
        label: measurementsVisible ? "Hide measurements" : "Show measurements",
        icon: "straighten",
        active: measurementsVisible,
        // The Unity command for this isn't wired yet — step 8 lands it; for
        // now the host's setFeature provides the UI feedback and the camera
        // verb path is the wiring template for when Unity exposes the verb.
        run: () => host.setFeature("measurementsVisible", !measurementsVisible),
      },
      {
        id: "help",
        label: "Help",
        icon: "help_outline",
        opensModal: "help",
      },
      {
        id: "open-cart",
        label: "View element list",
        icon: "list_alt",
        opensModal: "cart",
        enabled: host.instanceCount > 0,
      },
      {
        id: "close-cart",
        label: "Close",
        icon: "close",
        run: () => host.closeTopModal(),
      },
      {
        id: "back-to-series",
        label: "Back to series",
        icon: "arrow_back",
        run: () => host.backToSeriesPicker(),
        confirm: {
          title: "Back to the series picker?",
          message: "You'll leave the configurator and your current scene will be cleared. The action can't be undone.",
          confirmLabel: "Go back",
        },
      },
      {
        id: "download-model",
        label: "Download 3D model",
        icon: "download",
        opensModal: "download-model",
        enabled: host.instanceCount > 0,
      },
      {
        // Render the PDF Layout pointed to by the placing block's
        // pdfLayoutId prop and trigger a browser download. The action
        // delegates to host.triggerPdfDownload so the snapshot logic
        // (current scene state + Unity screenshot capture) stays in one
        // place. Disabled when nothing's been placed yet.
        id: "render-pdf-download",
        label: "Download PDF",
        icon: "picture_as_pdf",
        run: (blockProps) => {
          const id = typeof blockProps?.pdfLayoutId === "string" ? blockProps.pdfLayoutId : "";
          host.triggerPdfDownload(id);
        },
        enabled: host.instanceCount > 0,
        // Disable (not hide) when pdfLayoutId is missing so the operator
        // sees the button they placed and knows it needs configuring, rather
        // than wondering why nothing rendered.
        isDisabledForBlock: (blockProps) =>
          !blockProps || typeof blockProps.pdfLayoutId !== "string" || blockProps.pdfLayoutId.length === 0,
      },
      {
        // Same as render-pdf-download but opens the pdf-email modal instead
        // of firing the download. The modal is slot-driven (modal:pdf-email);
        // the form's submit block fires render-pdf-email-send to actually
        // dispatch the email.
        id: "render-pdf-email",
        label: "Email PDF",
        icon: "email",
        run: (blockProps) => {
          host.openPdfEmail(blockProps ?? {});
        },
        enabled: host.instanceCount > 0,
        // Enabled when the button carries either an emailLayoutId (the email is
        // the anchor) or a pdfLayoutId (legacy PDF-anchored email). Disabled when
        // neither is set so the operator knows the button needs configuring.
        isDisabledForBlock: (blockProps) => {
          const hasEmail = typeof blockProps?.emailLayoutId === "string" && blockProps.emailLayoutId.length > 0;
          const hasPdf = typeof blockProps?.pdfLayoutId === "string" && blockProps.pdfLayoutId.length > 0;
          return !hasEmail && !hasPdf;
        },
      },
      {
        // Form-submit verb wired by the modal:pdf-email slot's form-submit
        // block. blockProps carries pdfLayoutId (set on the form-submit
        // block — passes through from the operator) + formValues (collected
        // from the input blocks). The host implementation builds the
        // PdfEmailFields, calls sendPdfEmail(), then closes the modal.
        id: "render-pdf-email-send",
        label: "Send email",
        icon: "send",
        run: (blockProps) => {
          // Fire-and-forget — the action interface is sync. Errors surface
          // through the busy overlay + console.
          void host.sendPdfFromForm(blockProps ?? {});
        },
        enabled: true,
      },
      {
        // The block's renderProfileId is read inside RenderSceneModal — we
        // don't have it in the ActionDef factory here, so `enabled` only
        // gates on "is there something to render". The modal additionally
        // refuses to fire the POST when the profile id is missing.
        id: "render-scene",
        label: "Render scene",
        icon: "photo_camera",
        opensModal: "render-scene",
        enabled: host.instanceCount > 0,
      },
      {
        // Toggle the selected instance between its main variant and a
        // configured alternate. The variant key lives on the placing block
        // (e.g. "open"); read off blockProps. Toggle direction is computed
        // by the host based on the instance's current variant.
        // Per-block gate (isDisabledForBlock) checks the selected product's
        // detail cache: disables when there's no selection or the product
        // doesn't expose the target variant.
        id: "toggle-variant",
        label: "Toggle variant",
        // Static fallback. The dynamic icon below normally wins for placed
        // blocks; this only renders when iconForBlock returns undefined
        // (e.g. a block with no variantKey set yet — already disabled).
        icon: "swap_horiz",
        // `active` mirrors "instance is on the alternate" so the CSS
        // inverted-treatment fires while the block is the click-to-close.
        active: host.selectedInstanceVariantKey !== undefined,
        // Hide on products that simply don't have the variant — that's
        // not transient and the empty spot makes the popup feel cleaner.
        // Until the product detail is warmed we err on the side of hiding
        // too (the warm fetch completes in a few hundred ms; if it had
        // landed and the variant exists, the button would already be back).
        // Once warmed AND the variant exists, this returns false and the
        // button renders.
        isHiddenForBlock: (blockProps) => {
          const targetKey = typeof blockProps?.variantKey === "string"
            ? blockProps.variantKey : "";
          if (!targetKey) return true;
          return !host.canToggleVariant(targetKey);
        },
        // Material Symbols swap: bottom_panel_close when clicking will
        // open the panel (default / closed → alternate), top_panel_close
        // when clicking will close it (alternate → main). Reads the
        // current variant from the host and compares with the block's
        // configured key.
        iconForBlock: (blockProps) => {
          const targetKey = typeof blockProps?.variantKey === "string"
            ? blockProps.variantKey : "";
          if (!targetKey) return undefined;
          const onAlternate = host.selectedInstanceVariantKey === targetKey;
          return onAlternate ? "top_panel_close" : "bottom_panel_close";
        },
        run: (blockProps) => {
          const targetKey = typeof blockProps?.variantKey === "string"
            ? blockProps.variantKey : "";
          if (!targetKey) {
            console.warn("[SceneDesigner] toggle-variant clicked without a variantKey on the block.");
            return;
          }
          host.toggleVariant(targetKey);
        },
      },
      {
        id: "open-scene-looks",
        // Tool-rail entry point for the scene-looks picker modal. The modal's
        // own content (presets) lives in the modal:scene-looks slot — each
        // preset is an action block bound to apply-scene-materials.
        label: "Scene looks",
        icon: "palette",
        opensModal: "scene-looks",
      },
      {
        id: "apply-scene-materials",
        // Repaints one or more named Unity scene objects with DMats. The
        // bindings come from the placing ActionBlock's `dmatBindings` prop
        // (see ActionBlock for the forwarding). One Unity inbox call per
        // binding — the WebModelReceiver.ApplyMaterialToObject endpoint
        // wants the full DMat blob, so we fetch it before sending. Each
        // binding fires independently: a missing DMat or scene object
        // logs-and-skips rather than aborting the whole apply.
        label: "Apply scene look",
        icon: "format_paint",
        run: (blockProps) => {
          const raw = blockProps?.dmatBindings;
          if (!Array.isArray(raw) || raw.length === 0) {
            console.warn("[SceneDesigner] apply-scene-materials clicked with no dmatBindings on the block; nothing applied.");
            return;
          }
          let fired = 0;
          for (const b of raw) {
            if (!b || typeof b !== "object") continue;
            const r = b as Record<string, unknown>;
            const objectName = typeof r.objectName === "string" ? r.objectName : "";
            const dmatId     = typeof r.dmatId === "string" ? r.dmatId : "";
            // Optional material slot for multi-material objects (e.g. an
            // [ENVIRONMENT] mesh with wall on slot 0, floor on slot 1).
            // Absent → Unity defaults to slot 0.
            const slot = typeof r.slot === "number" ? r.slot : undefined;
            if (!objectName || !dmatId) {
              console.warn("[SceneDesigner] apply-scene-materials: skipping binding with missing objectName/dmatId:", b);
              continue;
            }
            // Fire-and-forget — each call awaits its own fetch + send chain.
            // The action's run is sync by contract.
            void host.applyMaterialToObject(objectName, dmatId, slot);
            fired++;
          }
          console.log(`[SceneDesigner] apply-scene-materials: fired ${fired}/${raw.length} binding(s).`);
        },
      },
      {
        id: "set-fps-limit",
        // Caps Unity's frame rate (battery / heat on mobile). Reads `fps` off
        // the placing block; 0 = uncapped. Typically placed in the `startup`
        // slot so the cap applies as soon as the configurator is ready, but it
        // also works as a tool-rail button. Unity-side: FpsLimitReceiver on the
        // WebBridge GameObject handles SetFpsLimit { fps }.
        label: "Set FPS limit",
        icon: "speed",
        run: (blockProps) => {
          const raw = blockProps?.fps;
          const fps = typeof raw === "number" && Number.isFinite(raw) && raw >= 0
            ? Math.floor(raw)
            : null;
          if (fps == null) {
            console.warn("[SceneDesigner] set-fps-limit: no valid `fps` on the block; nothing sent.");
            return;
          }
          host.sendCommand("SetFpsLimit", { fps });
        },
      },
    ];

    for (const a of builtins) map.set(a.id, a);
    return map;
  }, [host, measurementsVisible]);
}
